import { Overlay2 } from "@blueprintjs/core";
import { useRef, useState } from "react";
import "../templates/EditNameOverlay.css";
import { createStandaloneToast } from "@chakra-ui/react";
import api from "../../api";
import { themeColours } from "../../resources";
import { MdEdit } from "react-icons/md";
import { useStateObject } from "../../hooks/useStateObject";
import useSignIn from "react-auth-kit/hooks/useSignIn";
import Cookies from "js-cookie";
import { UserInfo } from "../../types";
import { useNavigate } from "react-router-dom";
import "./EditUserOverlay.css";
import { FaRegUserCircle } from "react-icons/fa";
import { FiEye } from "react-icons/fi";
import { FiEyeOff } from "react-icons/fi";

const { toast } = createStandaloneToast();

export function EditUserOverlay({ userInfo }: { userInfo: UserInfo }) {
  const [isOpen, setIsOpen] = useState(false);
  const { firstName, lastName, username, email, userId, createdAt } = userInfo;
  const signIn = useSignIn();
  const navigate = useNavigate();
  const passwordRef = useRef<HTMLInputElement>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [tempUserInfo, setTempUserInfo] = useStateObject<{
    firstName: string;
    lastName: string;
    username: string;
    email: string;
    password: string;
  }>({
    firstName,
    lastName,
    username,
    email,
    password: "",
  });
  const [isClosed, setIsClosed] = useState(true);

  function handleOpenClick() {
    if (localStorage.getItem("isGuest") !== "T") {
      setIsOpen(true);
      setIsClosed(false);
    }
  }

  function handleCloseClick() {
    setIsOpen(false);
    setIsClosed(true);
  }

  function handleNameChange(field: string, value: string) {
    setTempUserInfo({ [field]: value });
  }

  function handleShowPassword() {
    const newShowPassword = !showPassword;
    setShowPassword(newShowPassword);
    if (passwordRef.current) {
      passwordRef.current.type = newShowPassword ? "text" : "password";
    }
  }

  async function handleSave() {
    api.post("/account/update", tempUserInfo);
    handleCloseClick();
    toast({
      title: "User info successfully updated!",
      status: "success",
      duration: 5000,
      isClosable: true,
      position: "top",
      variant: "subtle",
    });
    const token = Cookies.get("_auth") as string;
    const isSignedIn = signIn({
      auth: { token, type: "Bearer" },
      userState: {
        firstName: tempUserInfo.firstName,
        lastName: tempUserInfo.lastName,
        username: tempUserInfo.username,
        userId,
        createdAt,
        email: tempUserInfo.email,
      },
    });
    if (isSignedIn) return navigate("/");
  }

  return (
    <>
      <span onClick={handleOpenClick} className="editNameSpan">
        <MdEdit size={30} color={themeColours.primary} />
      </span>
      {isOpen && (
        <Overlay2 isOpen={isOpen} className={isClosed ? "" : "modalOverlay"} hasBackdrop={true}>
          <div className="modalContainer">
            <div className="editUserModalContent">
              <FaRegUserCircle size={50} color={themeColours.primary} />
              <input
                placeholder="First Name"
                value={tempUserInfo.firstName}
                onChange={(e) => handleNameChange("firstName", e.target.value)}
              />
              <input
                placeholder="Last Name"
                value={tempUserInfo.lastName}
                onChange={(e) => handleNameChange("lastName", e.target.value)}
              />
              <input
                placeholder="Username"
                value={tempUserInfo.username}
                onChange={(e) => handleNameChange("username", e.target.value)}
              />
              <span className="editUserPasswordSpan">
                <input
                  placeholder="Password"
                  ref={passwordRef}
                  type="password"
                  value={tempUserInfo.password}
                  onChange={(e) => handleNameChange("password", e.target.value)}
                />
                {showPassword ? (
                  <FiEye size={15} color={themeColours.primary} onClick={handleShowPassword} />
                ) : (
                  <FiEyeOff size={15} color={themeColours.primary} onClick={handleShowPassword} />
                )}
              </span>
              <input
                placeholder="Email"
                value={tempUserInfo.email}
                onChange={(e) => handleNameChange("email", e.target.value)}
              />
              <span className="editUserButtonSpan">
                <button className="secondaryButton" onClick={handleCloseClick}>
                  Cancel
                </button>
                <button className="primaryButton" onClick={handleSave}>
                  Save
                </button>
              </span>
            </div>
          </div>
        </Overlay2>
      )}
    </>
  );
}
